import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';
import { getStudios, incrementStudioClick } from '@/api/studios';
import { AddStudioModal } from '@/components/AddStudioModal';
import { AppShell } from '@/components/AppShell';
import { SearchBox } from '@/components/SearchBox';
import { StudioDrawer } from '@/components/StudioDrawer';
import { StudioList } from '@/components/StudioList';
import { STUDIO_DRAWER_SEARCH_PARAM } from '@/constants/search-params';
import { studioKeys } from '@/lib/query-keys';
import { homeSearchSchema, type HomeSearch } from '@/lib/validation';
import type { Studio } from '@/types';

export const Route = createFileRoute('/')({
  validateSearch: (search) => homeSearchSchema.parse(search),
  component: HomeRoute,
});

function HomeRoute() {
  const [query, setQuery] = useState('');
  const [isAddStudioModalOpen, setIsAddStudioModalOpen] = useState(false);
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const studiosQuery = useQuery({
    queryKey: studioKeys.lists(),
    queryFn: getStudios,
  });

  const studios = studiosQuery.data ?? [];
  const selectedStudioId = search[STUDIO_DRAWER_SEARCH_PARAM];
  const selectedStudio = useMemo(() => {
    if (!selectedStudioId) {
      return null;
    }

    return studios.find((studio) => studio.id === selectedStudioId) ?? null;
  }, [selectedStudioId, studios]);

  const normalizedQuery = query.trim().toLowerCase();
  const filteredStudios = useMemo(() => {
    if (!normalizedQuery) {
      return studios;
    }

    return studios.filter((studio) => {
      return `${studio.title} ${studio.location}`.toLowerCase().includes(normalizedQuery);
    });
  }, [normalizedQuery, studios]);

  const openStudioDrawer = useCallback(
    (studio: Studio) => {
      void navigate({
        resetScroll: false,
        search: (previous: HomeSearch) => ({
          ...previous,
          [STUDIO_DRAWER_SEARCH_PARAM]: studio.id,
        }),
      });

      incrementStudioClick(studio.id).catch((error: unknown) => {
        console.warn('Failed to increment studio click count.', error);
      });
    },
    [navigate],
  );

  const closeStudioDrawer = useCallback(() => {
    void navigate({
      replace: true,
      resetScroll: false,
      search: (previous: HomeSearch) => {
        const nextSearch = { ...previous };
        delete nextSearch[STUDIO_DRAWER_SEARCH_PARAM];
        return nextSearch;
      },
    });
  }, [navigate]);

  return (
    <AppShell aiIdPrefix="home-page-shell">
      <section className="space-y-4" data-ai-id="home-page-search-and-list-section">
        <SearchBox value={query} onChange={setQuery} aiIdPrefix="home-page-search-field" />
        <div data-ai-id="home-page-studio-list-region">
          {studiosQuery.isPending ? (
            <div className="rounded-2xl border border-slate-200 bg-white px-5 py-8 text-center text-sm text-slate-500" data-ai-id="home-page-studio-list-loading-state">
              <span data-ai-id="home-page-studio-list-loading-state-text">연습실을 불러오는 중이에요.</span>
            </div>
          ) : studiosQuery.isError ? (
            <div className="rounded-2xl border border-red-100 bg-red-50 px-5 py-8 text-center text-sm text-red-700" data-ai-id="home-page-studio-list-error-state">
              <span data-ai-id="home-page-studio-list-error-state-text">연습실 목록을 불러오지 못했어요. 잠시 후 다시 시도해주세요.</span>
            </div>
          ) : (
            <StudioList studios={filteredStudios} onOpenStudio={openStudioDrawer} aiIdPrefix="home-page-studio-list" />
          )}
        </div>

        <div className="pt-2" data-ai-id="home-page-add-studio-cta">
          <button
            type="button"
            onClick={() => setIsAddStudioModalOpen(true)}
            className="flex h-12 w-full items-center justify-center rounded-2xl border border-emerald-200 bg-emerald-50 px-4 text-sm font-bold text-emerald-700 transition hover:bg-emerald-100 hover:text-emerald-800 focus:outline-none focus-visible:ring-4 focus-visible:ring-emerald-100"
            data-ai-id="home-page-add-studio-cta-button"
          >
            <span data-ai-id="home-page-add-studio-cta-button-label-text">연습실 추가</span>
          </button>
        </div>
      </section>

      <StudioDrawer isOpen={selectedStudio !== null} studio={selectedStudio} onClose={closeStudioDrawer} aiIdPrefix="home-page-studio-drawer" />
      <AddStudioModal
        isOpen={isAddStudioModalOpen}
        onClose={() => setIsAddStudioModalOpen(false)}
        aiIdPrefix="home-page-add-studio-modal"
      />
    </AppShell>
  );
}
